import { parseMoneyToCents } from './money'
import { sizeCmFromName } from './pizza-config'
import { tt } from './i18n/translation-memory'

/**
 * The Kombo BOX: three pizzas in one box, priced as one line.
 *
 * **The price is the dearest of the three, not their sum** — legacy's `Math.max(...)`, and it is the
 * offer rather than a bug: a kombo is one large pizza's worth of box with three halves in it, and
 * whichever is dearest sets what it costs.
 *
 * **The size is the kombo size, not each pizza's own.** `getPriceForKombo` looks for the pizza's price
 * at the configured kombo size, falls back to its **largest** size, and only then to its base price.
 * That chain matters and is kept exactly: a pizza sold in 32 and 40 cm has no 50 cm price, and charging
 * its 32 cm price inside a 50 cm box would sell the box at a third off.
 *
 * All money is integer cents (§14.7). Legacy uses `parseFloat` throughout, which is what the rule exists
 * to prevent.
 */

export interface KomboPizza {
  id: number
  name: string
  price: unknown
  /** The per-size price list, as the API sends it: a JSON string, or already parsed. */
  pizzaSizes?: unknown
  active?: boolean
}

export interface KomboSize {
  label: string
  price: unknown
}

/** The size list, whether it arrives as JSON text or as an array. */
export function sizesOf(pizza: KomboPizza): KomboSize[] {
  const raw = pizza.pizzaSizes
  const parsed = typeof raw === 'string' ? safeParse(raw) : raw
  if (!Array.isArray(parsed)) return []
  return parsed.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const row = entry as Record<string, unknown>
    return [{ label: typeof row.label === 'string' ? row.label : '', price: row.price }]
  })
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

/**
 * What one pizza costs inside a kombo box of this size, in cents.
 *
 * Exact size match, then the largest size on offer, then the pizza's base price — legacy's order, kept.
 */
export function komboPriceCents(pizza: KomboPizza, komboCm: number): number {
  const sizes = sizesOf(pizza)
  const exact = sizes.find((size) => sizeCmFromName(size.label) === komboCm)
  if (exact !== undefined) return parseMoneyToCents(exact.price as string | number | null)
  const largest = sizes[sizes.length - 1]
  if (largest !== undefined) return parseMoneyToCents(largest.price as string | number | null)
  return parseMoneyToCents(pizza.price as string | number | null)
}

export type KomboSlots = readonly (number | null)[]

export const EMPTY_KOMBO: KomboSlots = [null, null, null]

export function isComplete(slots: KomboSlots): boolean {
  return slots.length > 0 && slots.every((slot) => slot !== null)
}

/**
 * The box's price: the dearest slot, in cents.
 *
 * Zero until every slot is filled — a part-built box has no price, and showing a running figure that
 * only ever goes up would read as the box costing that much already.
 */
export function komboTotalCents(
  slots: KomboSlots,
  pizzas: readonly KomboPizza[],
  komboCm: number,
): number {
  if (!isComplete(slots)) return 0
  const prices = slots.map((slot) => {
    const pizza = pizzas.find((candidate) => candidate.id === slot)
    return pizza === undefined ? 0 : komboPriceCents(pizza, komboCm)
  })
  // A plain integer max: these are already cents (FB-056).
  return prices.reduce((dearest, price) => (price > dearest ? price : dearest), 0)
}

/** `Margherita + Šunková + Hawai` — the line's name, and the only description the kitchen gets. */
export function komboName(slots: KomboSlots, pizzas: readonly KomboPizza[]): string {
  return slots
    .map((slot) => pizzas.find((candidate) => candidate.id === slot)?.name ?? '')
    .filter((name) => name !== '')
    .join(' + ')
}

/**
 * Why the box cannot be added yet.
 *
 * An unfilled slot first, ahead of anything else — FB-190's lesson from the burger box, where the
 * config's own `required` rule was checked before the box was checked for being empty at all.
 */
export function komboProblem(slots: KomboSlots, pizzas: readonly KomboPizza[]): string {
  if (!isComplete(slots)) {
    const missing = slots.filter((slot) => slot === null).length
    return `${tt('Vyberte ešte pizze')}: ${missing}`
  }
  const unknown = slots.filter(
    (slot) => !pizzas.some((candidate) => candidate.id === slot && candidate.active !== false),
  )
  if (unknown.length > 0) return tt('Niektorá z vybraných pizz už nie je v ponuke.')
  return ''
}

/** The pizzas a slot may hold: on the menu, and priced. */
export function selectablePizzas(pizzas: readonly KomboPizza[]): KomboPizza[] {
  return pizzas.filter((pizza) => pizza.active !== false)
}

/**
 * `komboEnabled` and `komboSizeCm` used to live here as well, taking `unknown`.
 *
 * They were a second copy of `pizza-config`'s `isKomboEnabled` and `komboSizeCm` — same rule, same
 * defaults — and the two only coexisted because this file was in `customer-menu` and could not see
 * them. Moving it into the package put both in one namespace and the compiler said so, which is the
 * useful half of a move: a duplication nobody could see becomes a duplicate identifier.
 */


/**
 * The three pizzas a kombo is assembled from — `BACKEND.md` §10A.14.
 *
 * **Their sum is roughly three times what the kombo costs, and that is correct.** A kombo is priced
 * at the *dearest* of its three at the kombo's own size (`komboTotalCents`), which is the whole
 * offer. So the server's bound — which only ever corrects a line *down* — never fires for a kombo,
 * and is not meant to: it stops a line claiming more than what it declares, not less.
 *
 * What the parts are actually for here is the id on each one. Unlike a custom pizza, whose size and
 * toppings have no catalogue row, every kombo part names a real product — so the kitchen can be told
 * what to make (§9.55) and the server can re-price a part rather than take the figure sent.
 *
 * Empty slots are dropped rather than sent as blanks; an incomplete kombo cannot be added anyway.
 */
export function komboParts(
  slots: KomboSlots,
  pizzas: readonly KomboPizza[],
  komboCm: number,
): { productId: number; name: string; unitPriceCents: number; quantity: number }[] {
  return slots.flatMap((slot) => {
    const pizza = pizzas.find((candidate) => candidate.id === slot)
    return pizza === undefined
      ? []
      : [
          {
            productId: pizza.id,
            name: pizza.name,
            unitPriceCents: komboPriceCents(pizza, komboCm),
            quantity: 1,
          },
        ]
  })
}
